<template>
  <div v-loading="loading">
    <div class="fc-hero">
      <div>
        <div class="fc-hero__badge">Moliya kalendari</div>
        <h2 class="fc-hero__title">Kalendar</h2>
        <p class="fc-hero__sub">
          Portaldagi jadvalning aynan o'zi: qatorlar — ma'lumot turlari, ustunlar — oylar.
          Katakni bosib hodisa qo'shing, badge'ni bosib tahrirlang.
        </p>
      </div>
      <div class="fc-hero__actions">
        <el-button @click="copyVisible = true">Yilni nusxalash</el-button>
        <el-button type="primary" @click="openGenerator()">Generator</el-button>
      </div>
    </div>

    <div class="fc-toolbar">
      <div class="fc-toolbar__group">
        <el-select v-model="year" class="fc-year" placeholder="Yil" @change="fetchEvents">
          <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
        </el-select>
        <el-button plain title="Ro'yxatda yo'q yilni ochish" @click="promptYear">+ Yil</el-button>

        <el-select v-model="periodicityFilter" clearable placeholder="Davriylik bo'yicha" class="fc-filter">
          <el-option
              v-for="p in periodicities"
              :key="p.id"
              :label="periodicityTitle(p)"
              :value="p.id"
          />
        </el-select>

        <el-button plain @click="fetchAll">Yangilash</el-button>
      </div>

      <div class="fc-toolbar__group">
        <span class="fc-legend-title">Legend:</span>
        <span v-for="p in periodicities" :key="p.id" class="fc-legend" :class="{'is-off': p.is_active === false}">
          <span class="fc-legend__dot" :style="{background: p.color}"/>
          {{ periodicityTitle(p) }}
        </span>
      </div>
    </div>

    <MfEmpty
        v-if="!loading && !datasets.length"
        title="Ma'lumot turlari yo'q"
        description="Avval «Ma'lumot turlari» bo'limida kamida bitta qator yarating"
    />

    <div v-else class="fc-grid-wrap">
      <table class="fc-grid">
        <thead>
          <tr>
            <th class="fc-grid__rowhead">Ma'lumot turi</th>
            <th v-for="m in 12" :key="m" class="fc-grid__colhead">{{ monthLabel(m) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="dataset in datasets" :key="dataset.id">
            <th class="fc-grid__rowhead" :class="{'is-off': dataset.is_active === false}">
              <span class="fc-grid__rowtitle">{{ datasetTitle(dataset) }}</span>
              <el-tag v-if="dataset.is_active === false" size="small" type="info">nofaol</el-tag>
            </th>
            <td
                v-for="m in 12"
                :key="m"
                class="fc-grid__cell"
                @click="openCreate(dataset, m)"
            >
              <span
                  v-for="event in cellEvents(dataset.id, m)"
                  :key="event.id"
                  class="fc-badge"
                  :class="{'is-dim': isDimmed(event), 'is-off': event.is_active === false}"
                  :style="badgeStyle(event)"
                  :title="badgeTitle(event)"
                  @click.stop="openEdit(event)"
              >{{ dayText(event) }}</span>
              <span class="fc-grid__add">+</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="fc-cache-note">
      Portal sahifasi 5 daqiqa keshlanadi — bu yerdagi o'zgarish portalda darhol ko'rinmaydi.
      Admin ro'yxati esa keshlanmaydi.
    </p>

    <EventDialog
        v-model="eventVisible"
        :event="editingEvent"
        :datasets="datasets"
        :periodicities="periodicities"
        :defaults="eventDefaults"
        @saved="onEventSaved"
    />

    <GenerateDialog
        v-model="generateVisible"
        :datasets="datasets"
        :periodicities="periodicities"
        :year="generatorYear"
        @done="onToolDone"
    />

    <CopyYearDialog
        v-model="copyVisible"
        :years="years"
        :year="year"
        @done="onToolDone"
        @open-generator="openGenerator"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import MfEmpty from "@/components/core/MfEmpty.vue";
import EventDialog from "./EventDialog.vue";
import GenerateDialog from "./GenerateDialog.vue";
import CopyYearDialog from "./CopyYearDialog.vue";
import api, {
  displayLangText,
  monthName,
  dayLabel,
  contrastTextColor,
  apiErrorMessage,
  YEAR_MIN,
  YEAR_MAX,
} from "./financeCalendarApi";

export default {
  name: "FinanceCalendarGrid",
  components: { MfEmpty, EventDialog, GenerateDialog, CopyYearDialog },
  data() {
    return {
      loading: false,
      datasets: [],
      periodicities: [],
      events: [],
      years: [],
      // Hali hodisasi yo'q, admin qo'lda ochgan yillar.
      extraYears: [],
      year: new Date().getFullYear(),
      yearPicked: false,
      periodicityFilter: null,
      eventVisible: false,
      editingEvent: null,
      eventDefaults: {},
      generateVisible: false,
      generatorYear: null,
      copyVisible: false,
    };
  },
  computed: {
    // Admin endpointi faqat ma'lumot bor yillarni beradi — joriy yil va
    // qo'lda ochilgan yil ham ro'yxatda tursin.
    yearOptions() {
      const current = new Date().getFullYear();
      const set = new Set([...this.years, ...this.extraYears, current, this.year].filter(Boolean));
      return [...set].sort((a, b) => b - a);
    },
    // `${dataset_id}-${month}` -> hodisalar. Backend tartibi saqlanadi
    // (order_number, day_from, id) — qayta saralamaymiz.
    eventsByCell() {
      return this.events.reduce((acc, event) => {
        const key = `${event.dataset_id}-${event.month}`;
        (acc[key] = acc[key] || []).push(event);
        return acc;
      }, {});
    },
    periodicityById() {
      return this.periodicities.reduce((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {});
    },
  },
  created() {
    this.fetchAll();
  },
  methods: {
    datasetTitle(dataset) {
      return displayLangText(dataset.title) || `#${dataset.id}`;
    },
    periodicityTitle(periodicity) {
      return displayLangText(periodicity.title) || periodicity.code;
    },
    monthLabel(month) {
      return monthName(month, true);
    },
    cellEvents(datasetId, month) {
      return this.eventsByCell[`${datasetId}-${month}`] || [];
    },
    dayText(event) {
      return dayLabel(event.day_from, event.day_to);
    },
    badgeStyle(event) {
      const color = this.periodicityById[event.periodicity_id]?.color || "#94A3B8";
      return { background: color, color: contrastTextColor(color) };
    },
    // Portaldagidek: filtrga mos kelmagan badge o'chirilmaydi, xiralashadi.
    isDimmed(event) {
      return !!this.periodicityFilter && event.periodicity_id !== this.periodicityFilter;
    },
    badgeTitle(event) {
      const periodicity = this.periodicityTitle(this.periodicityById[event.periodicity_id] || {});
      const report = displayLangText(event.report_period);
      const parts = [periodicity, report].filter(Boolean);
      if (event.is_active === false) parts.push("nofaol");
      return parts.join(" · ");
    },
    fetchAll() {
      this.loading = true;
      Promise.all([api.datasets(), api.periodicities(), api.adminYears()])
          .then(([datasets, periodicities, years]) => {
            this.datasets = datasets;
            this.periodicities = periodicities;
            this.years = years;
            // Birinchi yuklashda ma'lumot bor eng oxirgi yilga tushamiz —
            // joriy yilda hech narsa bo'lmasa ham bo'sh jadval chiqmaydi.
            // Keyingi yangilashlarda adminning tanlovi saqlanadi.
            if (!this.yearPicked && years.length && !years.includes(this.year)) {
              this.year = years[0];
            }
            this.yearPicked = true;
            return this.fetchEvents();
          })
          .catch((error) => {
            ElNotification.error({ title: "Xatolik", message: apiErrorMessage(error) });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    // Eventlarda paginatsiya yo'q va filtrsiz so'rov hamma yilni tortadi —
    // shuning uchun `year` doim yuboriladi (CONSOLE.md §6).
    fetchEvents() {
      this.loading = true;
      return api
          .events({ year: this.year })
          .then((events) => {
            this.events = events;
          })
          .catch((error) => {
            ElNotification.error({ title: "Xatolik", message: apiErrorMessage(error) });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    // Bo'sh yilni ochish uchun: hodisa yo'q yil admin endpointida ham
    // ko'rinmaydi, shuning uchun uni qo'lda kiritish kerak.
    promptYear() {
      ElMessageBox.prompt("Qaysi yilni ochamiz?", "Yil", {
        confirmButtonText: "Ochish",
        cancelButtonText: "Bekor qilish",
        inputValue: String(this.year + 1),
        inputPattern: /^\d{4}$/,
        inputErrorMessage: "To'rt xonali yil kiriting",
      })
          .then(({ value }) => {
            const parsed = Number(value);
            if (parsed < YEAR_MIN || parsed > YEAR_MAX) {
              ElMessage.error(`Yil ${YEAR_MIN} va ${YEAR_MAX} orasida bo'lishi kerak`);
              return;
            }
            if (!this.extraYears.includes(parsed)) this.extraYears.push(parsed);
            this.year = parsed;
            this.fetchEvents();
          })
          .catch(() => {});
    },
    openCreate(dataset, month) {
      this.editingEvent = null;
      this.eventDefaults = {
        dataset_id: dataset.id,
        month,
        year: this.year,
        periodicity_id: this.periodicityFilter || this.periodicities[0]?.id || null,
      };
      this.eventVisible = true;
    },
    // List javobi to'liq ob'ekt qaytaradi, shuning uchun tahrirlashdan oldin
    // qo'shimcha GET kerak emas.
    openEdit(event) {
      this.editingEvent = event;
      this.eventDefaults = {};
      this.eventVisible = true;
    },
    openGenerator(year) {
      this.generatorYear = Number(year) || this.year;
      this.generateVisible = true;
    },
    // Hodisa boshqa yilga saqlangan bo'lsa grid o'sha yilga o'tadi — aks holda
    // admin saqlaydi-yu jadvalda hech narsa o'zgarmagandek ko'rinadi.
    onEventSaved(year) {
      this.switchYear(year);
      this.fetchEvents();
    },
    // Generator javobida `year`, copy-year javobida `to_year` keladi.
    onToolDone(result) {
      this.switchYear(result?.year || result?.to_year);
      this.fetchAll();
    },
    switchYear(year) {
      const parsed = Number(year);
      if (!parsed || parsed === this.year) return;
      if (!this.years.includes(parsed) && !this.extraYears.includes(parsed)) {
        this.extraYears.push(parsed);
      }
      this.year = parsed;
    },
  },
};
</script>

<style scoped lang="scss">
@use "./finance-calendar.scss";

.fc-year {
  width: 120px;
}

.fc-filter {
  width: 200px;
}

.fc-legend-title {
  font-size: 12px;
  color: #8a94a6;
}

.fc-legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4a5468;

  &.is-off {
    opacity: 0.45;
    text-decoration: line-through;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
}

.fc-grid-wrap {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  overflow-x: auto;
  background: #fff;
}

.fc-grid {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    border-bottom: 1px solid #f0f2f5;
    border-right: 1px solid #f0f2f5;
  }

  &__rowhead {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 220px;
    max-width: 260px;
    padding: 10px 14px;
    text-align: left;
    font-weight: 600;
    color: #303133;
    background: #fafbfd;

    &.is-off {
      opacity: 0.55;
    }
  }

  &__rowtitle {
    display: inline-block;
    margin-right: 6px;
  }

  &__colhead {
    padding: 10px 8px;
    min-width: 84px;
    font-weight: 600;
    color: #6b7485;
    background: #fafbfd;
    text-align: center;
  }

  &__cell {
    position: relative;
    padding: 8px 6px;
    vertical-align: top;
    text-align: center;
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
      background: #f7fbfc;

      .fc-grid__add {
        opacity: 1;
      }
    }
  }

  &__add {
    display: block;
    margin-top: 2px;
    font-size: 14px;
    line-height: 1;
    color: #b3bcc9;
    opacity: 0;
    transition: opacity 0.12s ease;
  }
}

.fc-badge {
  display: inline-block;
  margin: 2px;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.12s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &.is-dim {
    opacity: 0.3;
  }

  &.is-off {
    outline: 2px dashed rgba(0, 0, 0, 0.25);
    outline-offset: -2px;
  }
}

.fc-cache-note {
  margin-top: 12px;
  font-size: 12px;
  color: #8a94a6;
  text-align: center;
}
</style>
